<template>
  <div>
    <div class="mask" :class="{ maskActive: isOpen }" @click="closeSelf" />
    <!-- mb版 上滑popup -->
    <transition name="slide-up">
      <div
        v-if="dataLoad && isOpen"
        id="dialog-market"
        :class="{ active: isOpen }"
        class="dialog-bottom"
      >
        <div class="dialog-inner">
          <div class="cart-content">
            <a class="btn-delete" @click.prevent="closeSelf">
              <i class="icon-close" />
            </a>
            <div class="item-detail" @click.prevent="closeSelect">
              <div class="item-img">
                <NewImage
                  v-if="selectedFoodSpec.Product?.Images?.length !== 0"
                  :url="selectedProductCoverImage.Url"
                  :size="'_w752_h956'"
                  :name="allData.Name"
                />
              </div>
              <div class="item-info">
                <div class="item-order">
                  <div class="brand-name">
                    {{ allData.Brand?.Name }}
                  </div>
                  <div class="item-name">
                    {{ selectedFoodSpec.Product?.Name }}
                  </div>
                  <div class="item-price">
                    <span v-if="mapOriginPrice" class="origin-price">
                      NT$ {{ selectedFoodSpec.Product?.OriginPrice?.toLocaleString() }}
                    </span>
                    <span class="sell-price">NT$ {{ mapSellPrice }}</span>
                  </div>
                  <div v-if="selectedFoodSpec.Product?.LimitAmount !== 0" class="item-limit">
                    此商品限購 {{ selectedFoodSpec.Product?.LimitAmount }} 件
                  </div>
                  <ul class="item-event">
                    <li v-for="event in selectedFoodSpec.Product?.Events" :key="event.ID">
                      {{ event.Name }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="item-pick">
              <div v-if="!allData.ShowColorIcon" class="select-block">
                <div class="select-style">
                  <a class="select-btn" @click.prevent.stop="openSelector">
                    {{ userSelectFlavor.Name }}
                  </a>
                  <ul class="select-opts" :class="{ isopen: selectIsOpen }">
                    <li
                      v-for="(flavors, index) in allData.Flavors"
                      :key="flavors.Name"
                      @click.prevent="changeFlavor(index)"
                    >
                      {{ flavors.Name }}
                    </li>
                  </ul>
                </div>
                <FoodSpecSelector
                  :options="userSelectFlavor.Specs"
                  :selected="selectedFoodSpec"
                  @changeFoodSpec="selectSpec"
                />
              </div>
              <div v-else class="show-color">
                <p>
                  {{ userSelectFlavor.Title !== null ? userSelectFlavor.Title : '口味' }}：{{
                    userSelectFlavor.Name
                  }}
                </p>
                <div
                  v-for="(flavors, index) in allData.Flavors"
                  :key="flavors.Name"
                  class="color-wrap"
                  :class="{ active: flavors.Name === userSelectFlavor.Name }"
                  @click="changeFlavor(index)"
                >
                  <img :src="flavors.Specs[0]?.Product?.PintSizePictureUrl + '?w=20&h=20'" />
                </div>
                <div class="select-block">
                  <FoodSpecSelector
                    :options="userSelectFlavor.Specs"
                    :selected="selectedFoodSpec"
                    @changeFoodSpec="selectSpec"
                  />
                </div>
              </div>
              <Quantity
                class="mb-2"
                :counter="counter"
                :reduce-count="() => reduceCount()"
                :add-count="() => addCount()"
              />
              <button
                class="btn btn-primary w-100 btn-add2cart"
                :class="btnStyle"
                @click.prevent="addToCart"
              >
                {{ btnDescription }}
              </button>
            </div>
            <div v-if="selectedFoodSpec.Product?.PreOrder" class="product-arrival">
              {{ selectedFoodSpec.Product?.ArrivalExplanation }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, toRefs } from 'vue';

import gtmTrace from '@global/gtm/trace';
import { fireAddToCart, fireMemberFavoriteAddToCart } from '@global/gtm/common';

import useMiniCartStore from '@global/stores/useMiniCartStore';
import useModalStore from '@global/stores/useModalStore';

import useUtmKDetect from '@global/composable/useUtmKDetect';
import useMsg from '@global/composable/useMessage';
import useAlert from '@global/composable/useAlert';
import useDialog from '@composable/useDialog';
import useAxiosAPI from '@global/composable/api/useAxiosAPI';

import NewImage from '@global/components/common/Image/NewImage.vue';

import FoodSpecSelector from '@components/FoodSelect.vue';
import Quantity from '@components/Quantity.vue';

import { btnFoodDescriptionEnum, btnStyleEnum } from '@/models/productEnum';

const { utmKDetect, shareCode } = useUtmKDetect();
const { messageBox } = useMsg();
const { alertDialog } = useAlert();
const { foodDialogData, changeFoodOpen } = useDialog();
const { postShoppingCartAPI } = useAxiosAPI();

const modalStore = useModalStore();
const { openModal, closeModal } = modalStore;

const { isOpen, allData, dataLoad, listName } = toRefs(foodDialogData.value);

const selectedFoodSpec = ref(null);
const userSelectFlavor = ref(null);

const selectedProductCoverImage = computed(() => {
  return selectedFoodSpec.value.Product?.Images?.find((element) => element.Category === 11);
});

const mapOriginPrice = computed(() => {
  return (
    selectedFoodSpec.value.Product?.SellPrice !== selectedFoodSpec.value.Product?.OriginPrice ||
    selectedFoodSpec.value.Product?.EventPrice > 0
  );
});

const mapSellPrice = computed(() => {
  return selectedFoodSpec.value.Product?.EventPrice > 0 &&
    selectedFoodSpec.value.Product?.EventPrice < selectedFoodSpec.value.Product?.SellPrice
    ? selectedFoodSpec.value.Product?.EventPrice.toLocaleString()
    : selectedFoodSpec.value.Product?.SellPrice.toLocaleString();
});

watch(allData, () => {
  setSelectedFood(false);
});

/**
 * @description 取得 user 選擇的口味和規格
 * @param {boolean} type - true: user 手動變更口味 / false: 從列表頁進來，後端預設口味規格
 */
function setSelectedFood(type) {
  if (type === false) {
    userSelectFlavor.value = allData.value.Flavors?.find((flavor) => flavor.Selected);
    selectedFoodSpec.value = userSelectFlavor.value.Specs?.find((spec) => spec.Selected);
  } else {
    const checkHasStock = userSelectFlavor.value.Specs.find((stock) => stock.Stock > 0);

    if (checkHasStock) {
      selectedFoodSpec.value = checkHasStock;
    } else {
      // eslint-disable-next-line prefer-destructuring
      selectedFoodSpec.value = userSelectFlavor.value.Specs[0];
    }
  }

  btnActive();
}

const btnStyle = ref('disabled');
const btnDescription = ref('請選擇尺寸');
function btnActive() {
  if (selectedFoodSpec.value.Product?.IsPreShow) {
    btnStyle.value = btnStyleEnum.disabled;
    btnDescription.value = btnFoodDescriptionEnum.preShow;
    return;
  }

  if (selectedFoodSpec.value.Stock === 0) {
    if (selectedFoodSpec.value.Product?.PreOrder) {
      btnStyle.value = btnStyleEnum.disabled;
      btnDescription.value = btnFoodDescriptionEnum.soldOut;
    } else {
      btnStyle.value = btnStyleEnum.activeMail;
      btnDescription.value = btnFoodDescriptionEnum.notice;
    }
  } else {
    btnStyle.value = btnStyleEnum.active;
    btnDescription.value = btnFoodDescriptionEnum.addBag;
  }
}
function btnReset() {
  btnStyle.value = 'disabled';
  btnDescription.value = btnFoodDescriptionEnum.general;
}

const selectIsOpen = ref(false);
const openSelector = () => {
  selectIsOpen.value = !selectIsOpen.value;
};

function closeSelf() {
  changeFoodOpen(false);
  btnReset();
}

function closeSelect() {
  selectIsOpen.value = false;
}

function selectSpec(spec) {
  selectedFoodSpec.value = spec;
  resetCounter();
  btnActive();
}

function changeFlavor(flavorIndex) {
  if (allData.value.Flavors[flavorIndex].Name === userSelectFlavor.value.Name) return;

  selectIsOpen.value = false;
  userSelectFlavor.value = allData.value.Flavors[flavorIndex];
  setSelectedFood(true);
  btnActive();
  resetCounter();
}

const counter = ref(1);
function addCount() {
  const limit = selectedFoodSpec.value.Product?.LimitAmount;
  if (counter.value >= selectedFoodSpec.value.Stock) return;
  if (selectedFoodSpec.value.Product?.IsPreShow) return;
  if (counter.value >= limit && limit !== 0) return;
  counter.value += 1;
}
function reduceCount() {
  if (counter.value <= 1) return;
  counter.value -= 1;
}
function resetCounter() {
  counter.value = 1;
}

const miniCartStore = useMiniCartStore();
const { getCartInfo } = miniCartStore;
function addToCart() {
  if (selectedFoodSpec.value.Product?.IsPreShow) return;

  if (selectedFoodSpec.value === null) {
    alertDialog.alert(btnFoodDescriptionEnum.general);
    return;
  }

  if (selectedFoodSpec.value.Stock === 0) {
    if (selectedFoodSpec.value.Product?.PreOrder) {
      return;
    }

    openModal('arrivalNotice');
    return;
  }

  const utmData =
    utmKDetect(selectedFoodSpec.value.Product?.CustomMarketID) === null
      ? null
      : utmKDetect(selectedFoodSpec.value.Product?.CustomMarketID);

  const shareCodeData = shareCode() === null ? null : shareCode();

  postShoppingCartAPI({
    CustomMarketID: selectedFoodSpec.value.Product?.CustomMarketID,
    Count: counter.value,
    UtmTag: utmData,
    ShareCode: shareCodeData,
  })
    .then((res) => {
      if (res.data.Code === 200) {
        closeSelf();

        closeModal('foodMarket');

        messageBox.cart('商品加入購物車');

        getCartInfo();

        gtmTrace.addToCart({
          name: selectedFoodSpec.value.Product?.Name,
          id: selectedFoodSpec.value.Product?.CustomMarketID,
          price: selectedFoodSpec.value.Product?.OriginPrice,
          sale_price: mapSellPrice.value,
          img_url: selectedProductCoverImage.value?.Url,
          product_url: '',
          color: userSelectFlavor.value?.Name,
          count: counter.value,
        });

        fireAddToCart({
          item_id: selectedFoodSpec.value.Product?.CustomMarketID?.split('SZ')[0],
          item_name: allData.value?.Name,
          index: 0,
          affiliation: userSelectFlavor.value?.Name,
          item_brand: selectedFoodSpec.value?.Name,
          item_list_name: listName.value,
          price: mapSellPrice.value,
          quantity: counter.value,
          item_variant: '',
        });

        if (listName.value === '會員收藏清單') {
          fireMemberFavoriteAddToCart({
            productId: selectedFoodSpec.value.Product?.CustomMarketID?.split('SZ')[0],
            productName: allData.value.Name,
            addFavoriteItem: {
              item_id: selectedFoodSpec.value.Product?.CustomMarketID?.split('SZ')[0],
              item_name: allData.value.Name,
              index: 0,
              affiliation: userSelectFlavor.value.Name,
              item_brand: selectedFoodSpec.value.Name,
              item_list_name: listName.value,
              price: mapSellPrice.value,
              quantity: counter.value,
              item_variant: '',
            },
          });
        }
      } else if (res.data.Code === 205) {
        alertDialog.alert('商品已售完', () => {
          window.location.reload();
        });
      }
    })
    .catch((error) => {
      console.log(error);
    });
}
</script>

<style></style>
